import i18n from "@/locales";
import api from "@/modules/api";
import { mergeIgnoreEmpty } from "@/modules/common";
import { useMate } from "@/stores/mate";
import { Mate, SessionMode, MateSession, ChatMessage } from "@/types/entity";
import { defineStore } from "pinia";
import { showLoadingToast, showSuccessToast } from "vant";
import { Router } from "vue-router";

const PAGE_SIZE = 999;

interface ChatState {
  sessionList: number[];
  mateId2PublicSession: { [mateId: string]: number };
  mateId2PrivateSessionList: { [mateId: string]: number[] };
  sessionMap: { [id: number]: MateSession };
  mateId2UnreadCount: { [mateId: number]: number };
  showPersonaSelectPopup: boolean;
  chatPrepare: {
    mate: Mate | null;
    showRoleSetting: boolean;
    sceneId?: number;
  };
}

export interface ChatSessionItem {
  id: number;
  avatarUrl: string;
  title: string;
  content: string;
  time: string;
  uuid: string;
  unread: number;
  isPrivate: 0 | 1;
  mateId: number;
}

export const getInterlocutorBySession = (mateSession: MateSession) => {
  const mateStore = useMate();
  const currentMateId = mateStore.currentMate?.id;
  if (!currentMateId) {
    return null;
  }
  return mateSession.matezz.filter((mate) => mate.id !== currentMateId)[0];
};
export const getUnreadCountBySession = (mateSession: MateSession) => {
  const mateStore = useMate();
  const currentMateId = mateStore.currentMate?.id;
  return mateSession.chatMatezz.filter(
    ({ mateId }) => mateId === currentMateId
  );
};
const buildSessionItem = (mateSession: MateSession) => {
  const mate = getInterlocutorBySession(mateSession);
  const chatMate = getUnreadCountBySession(mateSession);
  const unread = chatMate[0]?.unreadCount ?? 0;
  return {
    id: mateSession.id,
    avatarUrl:
      mateSession?.chatScene?.media?.url ??
      (mateSession.media ? mateSession.media?.url : mate?.mediaUrl),
    title: mateSession.title ? mateSession.title : mate?.name ?? "",
    content: mateSession.chatMessage?.text ?? "",
    time: mateSession.chatMessage?.dtCreate ?? mateSession.dtUpdate,
    uuid: mate?.uuid,
    mateId: mate?.id,
    unread,
    isPrivate: mateSession.isPrivate,
  } as ChatSessionItem;
};

export const useChatSession = defineStore("chatSession", {
  state: () => ({
    sessionList: [] as number[],
    mateId2PublicSession: {} as { [mateId: string]: number },
    mateId2PrivateSessionList: {} as { [mateId: string]: number[] },
    sessionMap: {} as { [id: number]: MateSession },
    mateId2UnreadCount: {} as { [mateId: number]: number },
    chatPrepare: {
      mate: null as Mate | null,
      showRoleSetting: false,
      sceneId: undefined,
    },
    showPersonaSelectPopup: false,

    // tone select
    showSelectToneModal: false,
    selectToneType: "mate" as "mate" | "narrator",
    selectToneCallBack: null as (() => Promise<void>) | null,
  }),
  getters: {
    totalUnreadCount(): number {
      return Object.values(this.mateId2UnreadCount).reduce(
        (acc, count) => acc + count,
        0
      );
    },
    sessionOverviewList: (state: ChatState): ChatSessionItem[] => {
      return state.sessionList
        .map((id) => state.sessionMap[id])
        .filter((session) => getInterlocutorBySession(session))
        .map((session) => {
          const item = buildSessionItem(session);
          item.unread = state.mateId2UnreadCount[item.mateId] ?? 0;
          return item;
        });
    },
    getPublicSessionByMateId:
      (state: ChatState) =>
      (mateId: number): ChatSessionItem | null => {
        if (mateId in state.mateId2PublicSession) {
          const session = state.sessionMap[state.mateId2PublicSession[mateId]];
          return buildSessionItem(session);
        } else {
          return null;
        }
      },
    getPrivateSessionListByMateId:
      (state: ChatState) =>
      (mateId: number): ChatSessionItem[] => {
        if (mateId in state.mateId2PrivateSessionList) {
          return state.mateId2PrivateSessionList[mateId]
            .map((id) => state.sessionMap[id])
            .filter((session) => session.chatMessage)
            .map(buildSessionItem);
        } else {
          return [];
        }
      },
  },
  actions: {
    async fetchSessionList(
      filter: { mateId?: number; isPrivate?: 0 | 1; myMateId?: number } = {}
    ) {
      const res = await api.readMateSessionPage({
        filter,
        sort: { field: "dtUpdate", order: "desc" },
        pagination: { page: 1, perPage: PAGE_SIZE },
      });
      return this.updateSessionMap(res.page.data);
    },
    async createChatSession(
      mateId: number,
      isPrivate: 0 | 1,
      chatSceneId?: number,
      personaId?: number,
      myMateId?: number
    ): Promise<MateSession> {
      const res = await api.createOneMateSession(
        mateId,
        isPrivate,
        chatSceneId,
        personaId,
        myMateId
      );
      return await this.getOrFetchMateSessionItem(res.item.id!);
    },
    async createPublicSession(mateId: number): Promise<MateSession> {
      const session = await this.createChatSession(mateId, 0);
      this.mateId2PublicSession[mateId] = session.id!;
      return session;
    },
    async createPrivateSession(
      mateId: number,
      sceneId?: number,
      personaId?: number,
      myMateId?: number
    ): Promise<MateSession> {
      const session = await this.createChatSession(
        mateId,
        1,
        sceneId,
        personaId,
        myMateId
      );
      if (mateId in this.mateId2PrivateSessionList) {
        this.mateId2PrivateSessionList[mateId].unshift(session.id!);
      } else {
        this.mateId2PrivateSessionList[mateId] = [session.id!];
      }
      return session;
    },
    async getOrFetchSessionOverviewList() {
      if (this.sessionList.length > 0) {
        return this.sessionList.map((id) => this.sessionMap[id]);
      }
      const sessions = await this.fetchSessionList();
      this.sessionList = sessions.map((s) => s.id!);
      return sessions;
    },
    async fetchSessionOverviewList() {
      const sessions = await this.fetchSessionList();
      this.sessionList = sessions.map((s) => s.id!);
    },
    async getOrFetchPublicSession(
      mateId: number,
      force = false
    ): Promise<MateSession | null> {
      if (!force && this.mateId2PublicSession[mateId]) {
        return this.sessionMap[this.mateId2PublicSession[mateId]];
      }
      const session = await this.fetchSessionList({ mateId, isPrivate: 0 });
      if (session.length === 0) {
        return null;
      }
      this.mateId2PublicSession[mateId] = session[0].id!;
      return this.sessionMap[this.mateId2PublicSession[mateId]];
    },
    async getOrFetchPrivateSessionList(
      mateId: number,
      force = false,
      myMateId?: number
    ): Promise<MateSession[]> {
      if (!force && this.mateId2PrivateSessionList[mateId]) {
        return this.mateId2PrivateSessionList[mateId].map(
          (id) => this.sessionMap[id]
        );
      }
      const session = await this.fetchSessionList({
        mateId,
        isPrivate: 1,
        myMateId,
      });
      this.mateId2PrivateSessionList[mateId] = session.map((s) => s.id!);
      return this.mateId2PrivateSessionList[mateId].map(
        (id) => this.sessionMap[id]
      );
    },
    async getOrFetchSessionByMateId(
      mateId: number,
      force = false,
      myMateId?: number
    ) {
      const promises = [];
      this.getOrFetchPublicSession(mateId, force);
      promises.push(this.getOrFetchPrivateSessionList(mateId, force, myMateId));
      await Promise.all(promises);
    },
    async isSessionExist(mateId: number): Promise<boolean> {
      await this.getOrFetchSessionByMateId(mateId);
      return (
        mateId in this.mateId2PublicSession ||
        (mateId in this.mateId2PrivateSessionList &&
          this.mateId2PrivateSessionList[mateId].length > 0)
      );
    },
    async getOrFetchMateSessionItem(
      id: number,
      force = false
    ): Promise<MateSession> {
      const mateStore = useMate();
      if (!force && this.sessionMap[id]) {
        this.sessionMap[id].chatScene?.characterzz?.forEach((v) => {
          if (v.mateId) {
            mateStore.mateIdMap.set(v.mateId, v.mate!);
          }
        });
        return this.sessionMap[id];
      }
      const res = await api.readOneMateSession(id);
      mateStore.updateMateMap(res.item.matezz!, false);
      res.item.chatScene?.characterzz?.forEach((v) => {
        if (v.mateId) {
          mateStore.mateIdMap.set(v.mateId, v.mate!);
        }
      });
      return this.updateSessionMap([res.item])[0];
    },
    async deletePrivateSession(mateId: number, id: number): Promise<void> {
      await api.deleteOneMateSession(id);
      this.mateId2PrivateSessionList[mateId] = this.mateId2PrivateSessionList[
        mateId
      ].filter((sessionId) => sessionId !== id);
    },
    disableCache(): void {
      this.sessionList = [];
      this.mateId2PublicSession = {};
      this.mateId2PrivateSessionList = {};
      this.mateId2UnreadCount = {};
    },
    setSessionAsRead(sessionId: number): void {
      const session = this.sessionMap[sessionId];
      const chatMate = getUnreadCountBySession(session);
      chatMate[0].unreadCount = 0;
    },
    async updateSession(...args: Parameters<typeof api.updateOneMateSession>) {
      const [sessionId, data] = args;
      const oldSession = this.sessionMap[sessionId];
      const res = await api.updateOneMateSession(sessionId, {
        matePersonaId: oldSession.matePersonaId,
        ...data,
      });
      this.updateSessionMap([res.item]);
    },
    updateSessionMap(sessions: MateSession[]) {
      return sessions.map((item) => {
        const target = this.sessionMap[item.id!];
        if (target) {
          mergeIgnoreEmpty(target, item);
        } else {
          this.sessionMap[item.id!] = item;
        }
        return target ?? item;
      });
    },
    updatePreviewMessageInSession(sessionId: number, chatMessage: ChatMessage) {
      // 更新聊天列表(ChatList.vue)中的最新消息
      const session = this.sessionMap[sessionId];
      if (session) {
        (session as MateSession).chatMessage = chatMessage;
      }
    },
    async updateSessionMode(
      sessionId: number,
      targetMate: Mate,
      mode: SessionMode
    ) {
      // 接口内会隐式创建默认场景图和默认特征
      const willGenerateDefaultScene = mode === 1 && !targetMate.sceneMedia;
      const { item } = await api.updateMateSessionMode(sessionId, { mode });
      this.updateSessionMap([item]);
      if (willGenerateDefaultScene) {
        const mateStore = useMate();
        const mate = await mateStore.fetchMateItem(targetMate.uuid);
        Object.assign(targetMate, mate);
      }
    },
    async getUnreadCount() {
      const { data } = await api.readChatMessageCount();
      this.mateId2UnreadCount = data.reduce((acc, item) => {
        acc[item.mateId] = item.unreadCount;
        return acc;
      }, {} as { [mateId: number]: number });
    },
    async goChat(
      router: Router,
      mateUUID: string,
      sessionId: number,
      replace = false
    ) {
      await router.push({
        name: "mateChat",
        params: {
          mateSessionId: sessionId,
          uuid: mateUUID,
        },
        replace: replace ? true : undefined,
      });
    },

    // tone select
    async openSelectToneModal(type: "mate" | "narrator" = "mate") {
      this.showSelectToneModal = true;
      this.selectToneType = type;
    },

    async onUpdateCharacterTone(chatSessionId: number, toneId: number) {
      const loading = showLoadingToast(i18n.global.t("Message.saving"));
      try {
        if (this.selectToneType === "narrator") {
          await this.updateSession(chatSessionId, {
            narratorToneId: toneId,
          });
        } else if (this.selectToneType === "mate") {
          await this.updateSession(chatSessionId, {
            mateToneId: toneId,
          });
        }
        this.showSelectToneModal = false;
        showSuccessToast({ message: i18n.global.t("Message.saveSuccess") });
        if (this.selectToneCallBack) {
          await this.selectToneCallBack();
          this.selectToneCallBack = null;
        }
        loading.close();
      } catch (e) {
        loading.close();
      }
    },
  },
});
